import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookProcessingStatusService } from '../api/bookProcessingStatusService';
import { booksManageService } from '../api/booksManageService';
import {
  BookProcessingJobStatus,
  BookProcessingJobType,
  type BookProcessingStatusDto,
  type EpubRendererType,
} from '../types/manageBooks';
import type { BookDto } from '../types/book';

export type SwitchPhase = 'idle' | 'switching' | 'indexing' | 'ready' | 'failed';

const POLL_INTERVAL_MS = 3000;
const READY_HOLD_MS = 5000;

interface UseRendererSwitchArgs {
  bookId: string;
}

export interface UseRendererSwitchResult {
  phase: SwitchPhase;
  /** Job types currently Pending or Running (displayed in the tooltip). */
  activeJobTypes: BookProcessingJobType[];
  /** LastError from the first Failed job (if any) — shown in the red-pill tooltip. */
  failedJobError: string | null;
  /** Whether the switch POST is currently in flight. */
  isSwitching: boolean;
  /**
   * Trigger the switch. On success the parent's list-query invalidation will
   * cause the new book row to appear; `newBook` is returned so callers can
   * show toast links.
   */
  switchTo: (targetType: EpubRendererType) => Promise<BookDto>;
}

function deriveActiveJobTypes(rows: BookProcessingStatusDto[]): BookProcessingJobType[] {
  const active = new Set<BookProcessingJobType>();
  for (const r of rows) {
    if (r.status === BookProcessingJobStatus.Pending || r.status === BookProcessingJobStatus.Running) {
      active.add(r.jobType);
    }
  }
  return Array.from(active);
}

function findFirstFailedError(rows: BookProcessingStatusDto[]): string | null {
  const failed = rows.find(r => r.status === BookProcessingJobStatus.Failed);
  return failed?.lastError ?? (failed ? 'Job failed (no error message recorded)' : null);
}

/**
 * Keep only the latest row per job type (by creationTime desc). The backend
 * may accumulate historical rows for a given book across multiple switches;
 * we care only about the most recent attempt per job type.
 */
function latestPerJobType(rows: BookProcessingStatusDto[]): BookProcessingStatusDto[] {
  const byType = new Map<BookProcessingJobType, BookProcessingStatusDto>();
  for (const r of rows) {
    const prev = byType.get(r.jobType);
    if (!prev || new Date(r.creationTime).getTime() > new Date(prev.creationTime).getTime()) {
      byType.set(r.jobType, r);
    }
  }
  return Array.from(byType.values());
}

export function useRendererSwitch({ bookId }: UseRendererSwitchArgs): UseRendererSwitchResult {
  const qc = useQueryClient();
  const [readyHoldActive, setReadyHoldActive] = useState(false);
  const readyTimerRef = useRef<number | null>(null);

  const mutation = useMutation<BookDto, Error, EpubRendererType>({
    mutationFn: (targetType) => booksManageService.switchRenderer(bookId, targetType),
    onSuccess: () => {
      // The new book has a new ID; the old book (this bookId) will now report
      // as archived on the next list fetch. The list query invalidation is
      // the parent page's responsibility via its own onSuccess wrapper.
      void qc.invalidateQueries({ queryKey: ['manage-books'] });
    },
  });

  const statusQuery = useQuery<BookProcessingStatusDto[], Error>({
    queryKey: ['bookProcessingStatus', bookId],
    queryFn: () => bookProcessingStatusService.listByBookId(bookId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.length === 0) return false;
      if (query.state.error) return false;
      const latest = latestPerJobType(data);
      const hasActive = latest.some(
        r => r.status === BookProcessingJobStatus.Pending || r.status === BookProcessingJobStatus.Running
      );
      return hasActive ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  const latest = statusQuery.data ? latestPerJobType(statusQuery.data) : [];
  const activeJobTypes = deriveActiveJobTypes(latest);
  const failedJobError = findFirstFailedError(latest);
  const hasFailure = failedJobError !== null;
  const hasActive = activeJobTypes.length > 0;
  const allTerminalAndAtLeastOne =
    latest.length > 0 &&
    latest.every(
      r => r.status !== BookProcessingJobStatus.Pending && r.status !== BookProcessingJobStatus.Running
    );

  // Schedule the ready → idle transition when we first see all-terminal-no-failure.
  useEffect(() => {
    if (allTerminalAndAtLeastOne && !hasFailure && !readyHoldActive) {
      setReadyHoldActive(true);
      readyTimerRef.current = window.setTimeout(() => {
        setReadyHoldActive(false);
        readyTimerRef.current = null;
      }, READY_HOLD_MS);
    }
    return () => {
      if (readyTimerRef.current !== null) {
        window.clearTimeout(readyTimerRef.current);
        readyTimerRef.current = null;
      }
    };
    // Only depend on the trigger inputs — re-running on hold toggle would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTerminalAndAtLeastOne, hasFailure]);

  let phase: SwitchPhase = 'idle';
  if (mutation.isPending) {
    phase = 'switching';
  } else if (hasFailure) {
    phase = 'failed';
  } else if (hasActive) {
    phase = 'indexing';
  } else if (readyHoldActive) {
    phase = 'ready';
  }

  return {
    phase,
    activeJobTypes,
    failedJobError,
    isSwitching: mutation.isPending,
    switchTo: mutation.mutateAsync,
  };
}
